/**
 * space_light_look — the TSL half of space light (shader gaps R4, 2026-09-30): receiver-side eclipses, planetary
 * rings and the Commons' orbital cloud shell. The rules and their CPU mirrors are pure (`eclipse.ts`, `rings.ts`,
 * `orbital_clouds.ts`, pinned by `verify:space-light`); this file turns them into nodes and meshes, and holds the one
 * per-frame controller (`SpaceLight`) SpaceModule and the bed drive.
 *
 *  - `EclipseSet` — the uniforms of ONE frame of reference (a planet's own frame: true metres around its centre, world
 *    orientation; or WORLD space for the key light's hook — `hookOrigin`) and `visAt(P)`: the limb-darkened K-annulus visibility of
 *    the sun behind ≤ 4 picked occluders (+ the planet itself for its ring and shell), times the ring's filtered
 *    transmittance. The SAME number multiplies the direct sun, the glint and the atmosphere's sun term of a receiver.
 *  - THE HULL HOOK: three r185's `AnalyticLightNode.setupShadow` multiplies a light's colour by
 *    `light.shadow.shadowNode` when the light casts and the object receives shadows — a custom node there renders NO
 *    shadow map. The sector's key light carries `hook.visAt(positionWorld)`, so every lit receiver (the player's hull,
 *    war hulls, stations, rocks) takes its own per-fragment visibility; the planets and world planets beyond 100 km are
 *    scaled IMPOSTORS (their render position is not where they are) and never receive it — they carry their own set.
 *  - Rings: two meshes (the half behind the limb plane, the half in front — rings.RING_ORDER) over one RingGeometry, a
 *    premultiplied OVER (CustomBlending One / OneMinusSrcAlpha, THE EMISSION-BLEND LAW), the profile at the pixel's
 *    radial footprint (`.level()` — explicit, no derivative inside a branch), Chandrasekhar's lit / unlit-side formulas,
 *    edge-on coverage.
 *  - Clouds: a transparent shell child of the SPINNING planet mesh (the same uv as the night lights), coverage from the
 *    worker bake cross-faded between two textures, carved into cells by a 3-D detail noise on the unit direction; the
 *    surface's cloud shadow and the shell share `OrbitalCloudBaker.maskNode`.
 */
import * as THREE from 'three/webgpu';
import {
  Fn, If, uniform, float, vec2, vec3, vec4, texture, uv, positionWorld, positionGeometry, cameraPosition, modelWorldMatrix,
  length, normalize, dot, abs, max, min, clamp, select, asin, acos, atan, sqrt, exp, log, log2, mix, smoothstep, fwidth,
  mx_fractal_noise_float, positionLocal, cameraProjectionMatrix, modelViewMatrix,
} from 'three/tsl';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { LD_RHO, LD_COEF, LD_K, pickOccluders, receiverVisibility, type EclipseSun, type Occluder } from './eclipse.js';
import {
  bakeRingProfile, RING_TEX_W, RING_MU_FLOOR, RING_G_DUST, RING_MS_GAIN, RING_PARTICLE_HEX, RING_DUST_HEX, RING_ORDER, ringShadowT,
  type RingProfile, type RingSpec, type V3,
} from './rings.js';
import { CLOUD_SHELL_K, CLOUD_DETAIL, CLOUD_KAPPA, CLOUD_TEX_W, CLOUD_TEX_H, BAKE_EVERY_S, cloudBlend, bakeClouds, type CloudBake } from './orbital_clouds.js';
import { DEPTH_CODE, BODY_LAYER_CODES, OVER_REACH, RING_MESH, FAR_OPAQUE_PULL_CODES, type BodyLayer } from './space_look.js';
import { disposeAfterSwap } from '../gpu_dispose.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;
type U<T> = { value: T };

// ── THE BODY LAYER LAW (2026-10-07, "white specks pop in and out of a planet's atmosphere from a distance") ─────────
/**
 * A body's shells do not ask the depth buffer where they stand against THEIR OWN planet — they are seated a fixed number
 * of depth CODES behind it or in front of it, in clip space.
 *
 * WHY. The sector camera is near 0.05 m / far 3,600 km, and every body past 100 km is drawn as a scaled impostor AT
 * 100 km (SpaceModule FAR_ANCHOR_M). Depth is 1 − near/z: at 100 km one 24-bit code — and one float32 step of the
 * vertex stage's clip z — is ~12 km of view distance, while a far planet's whole scaled sphere is 2–40 km deep. So the
 * planet's front surface and the BACK faces of its atmosphere / rim shells (which must hide behind the disc), its cloud
 * shell and its ring's two halves land on the same few codes: the depth test passes and fails in triangle-shaped
 * patches that change as the camera moves. An O'Neil shell that leaks integrates its chord THROUGH the planet and
 * saturates white — the specks. Measured in `verify/planet-shell-bed-probe.mjs` (the sector's own camera planes; the
 * older space-light bed never saw it — its near plane was 50 m), law off, WebGL2 / WebGPU: a charted world's disc
 * 38 % / 36 % white with 45 % / 35 % of it changing when the ship moved 9 m, Cindral 6.5 % / 22 %, Palewater 8 % / 27 %,
 * 8 % / 23 % of the Commons' clouds lost — and nothing beside a planet (the Commons from the launch ring is no impostor).
 *
 * THE RULE. `seatBodyLayer(material, 'behind')` / `seatBodyLayer(material, 'over', reach)` give a shell a vertex stage
 * whose clip z is the true one moved a few codes (`space_look.BODY_LAYER_CODES`) — a precision-PROPORTIONAL margin (a
 * code is ∝ z²: metres beside a planet, the whole far range at the anchor) over the ~2 codes two meshes can disagree by:
 *   'behind' (the rim, the atmosphere shell, the ring's back half): AWAY from the camera, never past the far plane —
 *            16 codes for an impostor, 4 for a real, near body (its limb haze must stay in front of the far planets
 *            seen through it);
 *   'over'   (the cloud shell, the ring's front half): 8 codes TOWARD the camera, and only at a vertex that stands over
 *            its own body (its sight line within `reach` of the centre) — elsewhere it stands with the 'behind' layers
 *            (an impostor: the far plane; a near body: its true depth), so away from its planet a far ring is ONE
 *            backdrop: its front half neither jumps in front of another world nor fights one.
 * It is exact because of where each layer stands: a back-face shell and the ring half beyond the planet's LIMB plane are
 * behind the visible cap on every shared view ray; the cloud shell's front faces and the ring half on the camera's side
 * of the limb plane are in front of it. Everything else (a ship, a station, a nearer rock) still occludes them by the
 * depth test. In clip space, never `polygonOffset`: a hardware bias can push a fragment past the far plane, where a float
 * depth buffer rejects it on some backends (the limb would vanish). Reversed depth (`?rz=1`, WebGPU) has the precision
 * and keeps the true position. A/B: `localStorage atlas:body-layers = 'off'` (materials built after it).
 * KNOWN LIMITS: an 'over' layer wins against a REAL object standing within its 8 codes in front of it over the body's
 * disc (a few km beside a planet; 51–100 km out for an impostor). And far bodies have NO ORDER AMONG THEMSELVES — every
 * one is an impostor at one distance, so that was never the depth buffer's to give: Ashfall, in front of Cindral from
 * the launch ring, is hidden inside Cindral's impostor, and a far body's shells and ring are a backdrop to EVERY other
 * far body — right when the other is nearer, wrong when it is really behind (a planet beyond a ring is drawn over the
 * ring). Open — `space.md` §14 gap 47 has the recipe.
 */
export { DEPTH_CODE, BODY_LAYER_CODES, OVER_REACH, RING_MESH, layerClipZ, type BodyLayer } from './space_look.js';
let bodyLayers: boolean | null = null;
export function bodyLayersOn(): boolean {
  if (bodyLayers === null) { try { bodyLayers = localStorage.getItem('atlas:body-layers') !== 'off'; } catch { bodyLayers = true; } }
  return bodyLayers;
}
let bodyLayerCodes: number | null = null;
/** Probes and beds: force the law on / off (null = read the kill switch again), and optionally ONE margin for every
 *  layer (the bed's sweep — how few codes still separate them). Applies to materials built AFTER it. */
export function setBodyLayers(on: boolean | null, codes: number | null = null): void { bodyLayers = on; bodyLayerCodes = codes; }
/**
 * THE FAR OPAQUE'S SEAT: an opaque CLASSIC material drawn at its true distance past ~1,100 km (an outer-belt arc) is
 * pulled `FAR_OPAQUE_PULL_CODES` depth codes toward the eye, so the sky pass at the far plane no longer paints over it.
 * The engine's polygon-offset backport keys the pipeline on it and turns the bias under reversed depth. `atlas:body-layers
 * = 'off'` leaves the material as it was. Returns the material.
 */
export function seatFarOpaque<M extends THREE.Material>(material: M): M {
  if (!bodyLayersOn()) return material;
  material.polygonOffset = true; material.polygonOffsetFactor = 0; material.polygonOffsetUnits = -FAR_OPAQUE_PULL_CODES;
  material.needsUpdate = true;
  return material;
}
/**
 * Seat a NODE material's vertex stage on a body layer (THE BODY LAYER LAW). The material's object must have the body's
 * centre at its own origin (the planet group's children do). `reach` — 'over' only: the radius (the object's LOCAL
 * units) around the centre's sight line within which the layer stands over its own body. Returns the material.
 */
export function seatBodyLayer<M extends THREE.Material>(material: M, layer: BodyLayer, reach = 0): M {
  const m = material as unknown as { isNodeMaterial?: boolean; vertexNode: N; userData: Record<string, unknown> };
  if (m.isNodeMaterial !== true) throw new Error('seatBodyLayer: a node material is required (a classic material has no vertex slot)');
  if (layer === 'over' && !(reach > 0)) throw new Error("seatBodyLayer: an 'over' layer needs its reach (the body's radius × space_look.OVER_REACH)");
  if (!bodyLayersOn()) return material;
  const sweep = bodyLayerCodes;
  m.vertexNode = Fn((builder: N) => {
    const posV = modelViewMatrix.mul(vec4(positionLocal, 1.0));
    const clip = cameraProjectionMatrix.mul(posV);
    const r = (builder as { renderer?: { reversedDepthBuffer?: boolean; coordinateSystem?: number } } | undefined)?.renderer;
    if (r?.reversedDepthBuffer === true) return clip; // reversed depth resolves the gap itself (and its far plane is 0)
    const glClip = r?.coordinateSystem === THREE.WebGLCoordinateSystem;
    const unit = clip.w.mul(DEPTH_CODE * (glClip ? 2 : 1));
    const k = length(modelWorldMatrix.mul(vec4(1, 0, 0, 0)).xyz); // the impostor scale (1 = a real, near body)
    if (layer === 'behind') {
      const codes = sweep !== null ? float(sweep) : select(k.lessThan(0.999), float(BODY_LAYER_CODES.behindFar), float(BODY_LAYER_CODES.behindNear));
      return vec4(clip.x, clip.y, min(clip.z.add(unit.mul(codes)), clip.w), clip.w);
    }
    // 'over': only where this vertex stands over its own body (space_look.overOwnBody, in view space)
    const cV = modelViewMatrix.mul(vec4(0, 0, 0, 1)).xyz;
    const dir = normalize(posV.xyz);
    const tca = dot(cV, dir);
    const c2 = dot(cV, cV);
    const r2 = k.mul(reach).mul(k.mul(reach));
    const own = c2.sub(tca.mul(tca)).lessThan(r2).and(tca.greaterThan(0).or(c2.lessThan(r2)));
    const zOver = max(clip.z.sub(unit.mul(sweep ?? BODY_LAYER_CODES.over)), glClip ? clip.w.negate() : float(0));
    // away from its own body the layer is a backdrop like the body's other shells: an impostor's on the far plane (it
    // neither jumps in front of another world nor fights one — both stand at one distance), a near body's at its true depth
    const zAway = select(k.lessThan(0.999), min(clip.z.add(unit.mul(BODY_LAYER_CODES.behindFar)), clip.w), clip.z);
    return vec4(clip.x, clip.y, select(own, zOver, zAway), clip.w);
  })();
  m.userData.bodyLayer = layer;
  return material;
}

// ── shared resources ─────────────────────────────────────────────────────────────────────────────────────────────
let profCpu: RingProfile | null = null;
let profTex: THREE.DataTexture | null = null;
/** The ring profile (CPU mirror + its RGBA8 texture), baked once per page (`userData.shared`). */
export function ringProfileCpu(): RingProfile { return (profCpu ??= bakeRingProfile(RING_TEX_W)); }
export function ringProfileTex(): THREE.DataTexture {
  if (profTex) return profTex;
  const p = ringProfileCpu();
  const t = new THREE.DataTexture(p.levels[0], p.w, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.wrapS = THREE.ClampToEdgeWrapping; t.wrapT = THREE.ClampToEdgeWrapping;
  t.magFilter = THREE.LinearFilter; t.minFilter = THREE.LinearMipmapLinearFilter; t.generateMipmaps = true;
  t.colorSpace = THREE.NoColorSpace;
  t.name = 'space-ring-profile';
  t.userData.shared = true;
  t.needsUpdate = true;
  profTex = t;
  return t;
}

// ── the eclipse math as nodes (the mirror of eclipse.ts) ─────────────────────────────────────────────────────────
/** Area of two intersecting discs (radii r1, r2, centres d apart) — space_look.discOverlapArea. */
function overlapN(r1: N, r2: N, d: N): N {
  const dd = max(d, float(1e-6));
  const a = clamp(dd.sub(r2).mul(dd.add(r2)).add(r1.mul(r1)).div(dd.mul(r1).mul(2)), -1, 1);
  const b = clamp(dd.sub(r1).mul(dd.add(r1)).add(r2.mul(r2)).div(dd.mul(r2).mul(2)), -1, 1);
  const k = r1.add(r2).sub(dd).mul(dd.add(r1).sub(r2)).mul(dd.sub(r1).add(r2)).mul(dd.add(r1).add(r2));
  const lens = r1.mul(r1).mul(acos(a)).add(r2.mul(r2).mul(acos(b))).sub(sqrt(max(k, float(0))).mul(0.5));
  const mn = min(r1, r2);
  return select(d.greaterThanEqual(r1.add(r2)), float(0), select(d.lessThanEqual(abs(r1.sub(r2))), mn.mul(mn).mul(Math.PI), lens));
}
/** eclipse.visibleFraction: the limb-darkened sun's visible fraction behind one occluder. */
export function visibleFractionN(aS: N, aO: N, sep: N): N {
  const o = aO.div(aS), dd = sep.div(aS);
  let blocked: N = float(0);
  for (let k = 0; k < LD_K; k++) blocked = blocked.add(overlapN(float(LD_RHO[k]), o, dd).mul(LD_COEF[k]));
  return select(dd.greaterThanEqual(o.add(1)), float(1), select(o.greaterThanEqual(1).and(dd.lessThanEqual(o.sub(1))), float(0), clamp(float(1).sub(blocked), 0, 1)));
}
/** eclipse.angleBetween (CosmoScout's small-angle-safe 2·asin(½|u − v|)). */
const angleN = (u: N, v: N): N => asin(clamp(length(u.sub(v)).mul(0.5), 0, 1)).mul(2);

/**
 * The uniforms of one frame of reference and the visibility node over it. Positions (occluders, the ring's centre,
 * a point sun) are in the frame; `visAt(P)` takes a point in the same frame.
 */
export class EclipseSet {
  /** xyz = the sun's DIRECTION (w 0, sys0 — at infinity) or POSITION (w 1, a derived system's star). */
  readonly uSun = uniform(new THREE.Vector4(0.5406, 0.7863, 0.2949, 0));
  /** The sun's angular radius (dir) or radius in metres (point). */
  readonly uSunSize = uniform(0.0096);
  readonly uOcc = [0, 1, 2, 3].map(() => uniform(new THREE.Vector4(0, 0, 0, 0)));
  /** The planet itself (a sphere at the frame's origin) — its shadow on its ring and shell. 0 = none. */
  readonly uSelfR = uniform(0);
  readonly uRingOn = uniform(0);
  readonly uRingC = uniform(new THREE.Vector3());
  readonly uRingN = uniform(new THREE.Vector3(0, 1, 0));
  /** x planet radius (m), y rIn, z rOut (planet radii), w optical-depth scale. */
  readonly uRingR = uniform(new THREE.Vector4(1, 1.11, 2.34, 1));
  /** 1 = eclipses on (the flag / the kill switch, live). */
  readonly uOn = uniform(1);
  /** The CPU mirror of what the uniforms hold (the same frame). */
  sun: EclipseSun = { kind: 'dir', dir: { x: 0.5406, y: 0.7863, z: 0.2949 }, angR: 0.0096 };
  occ: Occluder[] = [];
  ring: RingSpec | null = null;
  selfR = 0;
  constructor(readonly name: string) {}

  /** Seat the sun in this frame: `origin` is the frame's origin in the absolute frame the sun is given in. */
  setSun(sun: EclipseSun, origin: V3): void {
    const v = (this.uSun as unknown as U<THREE.Vector4>).value;
    if (sun.kind === 'dir') {
      v.set(sun.dir.x, sun.dir.y, sun.dir.z, 0); (this.uSunSize as unknown as U<number>).value = sun.angR;
      this.sun = sun;
    } else {
      const p = { x: sun.pos.x - origin.x, y: sun.pos.y - origin.y, z: sun.pos.z - origin.z };
      v.set(p.x, p.y, p.z, 1); (this.uSunSize as unknown as U<number>).value = sun.radius;
      this.sun = { kind: 'point', pos: p, radius: sun.radius };
    }
  }
  /** Occluders given in the ABSOLUTE frame, written relative to `origin` (≤ 4; the rest are dropped). */
  setOccluders(list: ReadonlyArray<Occluder>, origin: V3): void {
    this.occ = [];
    for (let i = 0; i < this.uOcc.length; i++) {
      const o = list[i];
      const v = (this.uOcc[i] as unknown as U<THREE.Vector4>).value;
      if (o) { v.set(o.x - origin.x, o.y - origin.y, o.z - origin.z, o.r); this.occ.push({ x: v.x, y: v.y, z: v.z, r: o.r, key: o.key }); }
      else v.set(0, 0, 0, 0);
    }
  }
  setSelf(r: number): void { this.selfR = r; (this.uSelfR as unknown as U<number>).value = r; }
  /** The ring spec in THIS frame (centre relative to the frame's origin), or null. */
  setRing(spec: RingSpec | null): void {
    this.ring = spec;
    (this.uRingOn as unknown as U<number>).value = spec ? 1 : 0;
    if (!spec) return;
    (this.uRingC as unknown as U<THREE.Vector3>).value.set(spec.centre.x, spec.centre.y, spec.centre.z);
    (this.uRingN as unknown as U<THREE.Vector3>).value.set(spec.normal.x, spec.normal.y, spec.normal.z);
    (this.uRingR as unknown as U<THREE.Vector4>).value.set(spec.planetR, spec.rIn, spec.rOut, spec.tauScale ?? 1);
  }
  on = true;
  setOn(on: boolean): void { this.on = on; (this.uOn as unknown as U<number>).value = on ? 1 : 0; }
  /** The CPU visibility at a point of this frame (the mirror the harness and the levers read — visAt's composition). */
  visibilityAt(P: V3, self = false, ring = true): number {
    const occ = [...(this.on ? this.occ : []), ...(self && this.selfR > 0 ? [{ x: 0, y: 0, z: 0, r: this.selfR }] : [])];
    return receiverVisibility(P, this.sun, occ, ring && this.ring ? { spec: this.ring, prof: ringProfileCpu() } : null);
  }

  /** The sun seen from P (this frame): its unit direction, angular radius and distance. */
  sunAt(P: N): { L: N; aS: N; dist: N } {
    const isPoint = this.uSun.w.greaterThan(0.5);
    const toS = this.uSun.xyz.sub(P);
    const dS = max(length(toS), float(1));
    const L = select(isPoint, toS.div(dS), normalize(this.uSun.xyz));
    const aS = select(isPoint, asin(clamp(this.uSunSize.div(dS), 0, 1)), this.uSunSize);
    return { L, aS, dist: select(isPoint, dS, float(1e30)) };
  }
  /** Where the sun ray from P crosses the ring plane, in planet radii (the footprint's derivative source). */
  ringRhoAt(P: N): N {
    const { L } = this.sunAt(P);
    const ln = dot(L, this.uRingN);
    const t = dot(this.uRingC.sub(P), this.uRingN).div(select(abs(ln).lessThan(1e-6), float(1e-6), ln));
    return length(P.add(L.mul(t)).sub(this.uRingC)).div(this.uRingR.x);
  }
  /** rings.ringShadowT as a node: the profile where the sun ray crosses the ring, filtered by the sun's penumbra there. */
  ringT(P: N, L: N, aS: N, footRho: N): N {
    const n = this.uRingN, R = this.uRingR.x, rIn = this.uRingR.y, rOut = this.uRingR.z;
    const ln = dot(L, n);
    const t = dot(this.uRingC.sub(P), n).div(select(abs(ln).lessThan(1e-6), float(1e-6), ln));
    const rho = length(P.add(L.mul(t)).sub(this.uRingC)).div(R);
    const span = rOut.sub(rIn);
    const u = rho.sub(rIn).div(span);
    const pen = t.mul(aS).mul(2).div(R);
    const foot = sqrt(pen.mul(pen).add(footRho.mul(footRho)));
    const lod = max(float(0), log2(max(foot.mul(RING_TEX_W).div(span), float(1e-6))));
    const T0 = (texture(ringProfileTex(), vec2(clamp(u, 0, 1), 0.5)) as N).level(lod).r;
    const tau = log(max(T0, float(1e-4))).negate().mul(this.uRingR.w);
    const T = exp(tau.negate().div(max(abs(ln), float(RING_MU_FLOOR))));
    const valid = this.uRingOn.greaterThan(0.5).and(t.greaterThan(0)).and(u.greaterThanEqual(0)).and(u.lessThanEqual(1)).and(abs(ln).greaterThan(1e-6));
    return select(valid, T, float(1));
  }
  /**
   * THE RECEIVER'S VISIBILITY at P: Π the picked occluders (the eclipse flag mixes them toward 1 — `uOn`) × the planet's
   * own shadow on its ring / shell (`self` — part of that body's own lighting, never switched off) × the ring's
   * transmittance (`ring` — the ring flag's; `uRingOn`). `footRho` — an extra radial footprint for the ring lookup.
   */
  visAt(P: N, o: { self?: boolean; ring?: boolean; footRho?: N } = {}): N {
    return Fn(() => {
      const s = this.sunAt(P);
      const one = (C: N, R: N): N => {
        const dv = C.sub(P);
        const d = max(length(dv), float(1e-3));
        const aO = asin(clamp(R.div(d), 0, 1));
        const sep = angleN(s.L, dv.div(d));
        return select(d.lessThanEqual(R), float(0), select(d.greaterThan(s.dist), float(1), visibleFractionN(s.aS, aO, sep)));
      };
      const vis = float(1).toVar();
      for (const u of this.uOcc) If(u.w.greaterThan(0), () => { vis.mulAssign(one(u.xyz, u.w)); });
      const out = mix(float(1), vis, this.uOn).toVar();
      if (o.self) If(this.uSelfR.greaterThan(0), () => { out.mulAssign(one(vec3(0, 0, 0), this.uSelfR)); });
      if (o.ring) out.mulAssign(this.ringT(P, s.L, s.aS, o.footRho ?? float(0)));
      return out;
    })();
  }
}

// ── frames ───────────────────────────────────────────────────────────────────────────────────────────────────────
/**
 * A mesh whose origin is a planet's centre (the planet, its shells, its ring, its cloud shell): the centre and the
 * impostor scale k from its world matrix, the fragment and the camera in TRUE metres around the centre (world
 * orientation — the sector root and the planet groups carry no rotation).
 */
export function planetFrame(): { centre: N; k: N; rel: N; camRel: N } {
  const centre = modelWorldMatrix.mul(vec4(0, 0, 0, 1)).xyz;
  const k = length(modelWorldMatrix.mul(vec4(1, 0, 0, 0)).xyz);
  return { centre, k, rel: positionWorld.sub(centre).div(k), camRel: cameraPosition.sub(centre).div(k) };
}
/** A shell fragment's LIMB point: the view ray's closest approach to the centre (true metres), never inside R. */
export function tangentRel(R: number): N {
  const f = planetFrame();
  const dir = normalize(positionWorld.sub(cameraPosition));
  const tca = dot(f.centre.sub(cameraPosition), dir);
  const Q = cameraPosition.add(dir.mul(tca)).sub(f.centre).div(f.k);
  const r = max(length(Q), float(1e-3));
  return Q.mul(max(r, float(R * 1.0005)).div(r));
}
/** Multiply an atmosphere shell's colour by the visibility at its limb point (the engine materials untouched). */
export function wrapShellVisibility(material: THREE.Material, set: EclipseSet, R: number): void {
  const m = material as unknown as { colorNode: N };
  const base = m.colorNode;
  if (!base) return;
  const vis = set.visAt(tangentRel(R), { ring: true });
  m.colorNode = vec4(base.rgb.mul(vis), base.a);
}

// ── rings ────────────────────────────────────────────────────────────────────────────────────────────────────────
export interface RingBuild { back: THREE.Mesh; front: THREE.Mesh; geometry: THREE.BufferGeometry; materials: THREE.Material[] }
const HG = (g: number, cosT: N): N => float(1 - g * g).div(max(float(1 + g * g).sub(cosT.mul(2 * g)), float(1e-6)).pow(1.5));
/**
 * Build a planet's ring: two meshes (back, front) over one RingGeometry in the plane ⟂ `normal`, to be added to the
 * planet GROUP (not the spinning mesh). `sunColor` the planets' shared sun radiance (planet_look.uPlanetSun),
 * `uPixel` one pixel's angle (the edge-on coverage).
 */
export function buildRing(o: { R: number; rIn: number; rOut: number; normal: V3; set: EclipseSet; sunColor: N; uPixel: N; gain?: number }): RingBuild {
  const geometry = new THREE.RingGeometry(o.rIn * o.R, o.rOut * o.R, RING_MESH.theta, RING_MESH.radial); // the mesh carries depth only (space_look.RING_MESH)
  const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), new THREE.Vector3(o.normal.x, o.normal.y, o.normal.z).normalize());
  const cP = new THREE.Color(RING_PARTICLE_HEX), cD = new THREE.Color(RING_DUST_HEX);
  const make = (half: 'back' | 'front'): THREE.Mesh => {
    const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide });
    mat.blending = THREE.CustomBlending;
    mat.blendSrc = THREE.OneFactor; mat.blendDst = THREE.OneMinusSrcAlphaFactor; mat.blendEquation = THREE.AddEquation;
    mat.blendSrcAlpha = THREE.OneFactor; mat.blendDstAlpha = THREE.OneMinusSrcAlphaFactor; mat.blendEquationAlpha = THREE.AddEquation;
    mat.fog = false;
    const set = o.set;
    mat.colorNode = Fn(() => {
      const f = planetFrame();
      const P = f.rel, C = f.camRel;
      // the radial coordinate in the ring's own plane (exact per fragment) and its pixel footprint — top of the shader
      const rho = length(positionGeometry.xy).div(o.R);
      const u = rho.sub(o.rIn).div(o.rOut - o.rIn);
      // .toVar() on both (2026-10-07): un-pinned, three emitted this fwidth and the profile sample where they were first
      // USED — three copies, each inside a branch of the lit / unlit select (read from the generated GLSL). A derivative
      // in non-uniform control flow is undefined: the footprint flickered where a branch changed inside a pixel quad.
      const lod = max(float(0), log2(max(fwidth(u).mul(RING_TEX_W), float(1e-6)))).toVar();
      const s = (texture(ringProfileTex(), vec2(clamp(u, 0, 1), 0.5)) as N).level(lod).toVar();
      const tau = log(max(s.r, float(1e-4))).negate().mul(set.uRingR.w);
      const albedo = s.g, dust = s.b;
      const n = set.uRingN;
      const V = normalize(C.sub(P));
      const sun = set.sunAt(P);
      const L = sun.L;
      const vn = dot(V, n), ln = dot(L, n);
      const mu = max(abs(vn), float(RING_MU_FLOOR)), mu0 = max(abs(ln), float(RING_MU_FLOOR));
      const cosA = dot(L, V);
      const alpha = acos(clamp(cosA, -1, 1));
      const lambertSphere = alpha.sin().add(float(Math.PI).sub(alpha).mul(cosA)).mul(8 / (3 * Math.PI));
      const phase = mix(lambertSphere, HG(RING_G_DUST, cosA.negate()), dust);
      const kk = albedo.mul(phase).mul(0.25);
      const litF = kk.mul(RING_MS_GAIN).mul(mu0.div(mu0.add(mu))).mul(float(1).sub(exp(tau.negate().mul(float(1).div(mu0).add(float(1).div(mu))))));
      const dm = mu.sub(mu0);
      const unlitF = select(abs(dm).lessThan(1e-3),
        kk.mul(tau.div(mu0)).mul(exp(tau.negate().div(mu0))),
        kk.mul(mu0.div(select(abs(dm).lessThan(1e-3), float(1e-3), dm))).mul(exp(tau.negate().div(mu)).sub(exp(tau.negate().div(mu0)))));
      const IF = select(vn.mul(ln).greaterThan(0), litF, max(unlitF, float(0)));
      const vis = set.visAt(P, { self: true });
      const col = vec3(o.sunColor).mul(IF).mul(vis).mul(mix(vec3(cP.r, cP.g, cP.b), vec3(cD.r, cD.g, cD.b), dust)).mul(o.gain ?? 1);
      const a = float(1).sub(exp(tau.negate().div(mu)));
      // edge-on: the sheet's projected radial width in pixels is its coverage (never a sparse dashed line)
      const widthW = f.k.mul((o.rOut - o.rIn) * o.R);
      const dist = max(length(cameraPosition.sub(positionWorld)), float(1));
      const cov = clamp(widthW.mul(abs(vn)).div(dist.mul(o.uPixel)), 0, 1);
      // THE SPLIT: this mesh draws only its half (the other half is the other mesh). The cut is the planet's LIMB plane
      // (P·C = R² — rings.ringHalf), not the plane through its centre: only then is the front half in front of the
      // visible cap and the back half behind it on EVERY shared view ray, which is what lets each stand on a body layer.
      const side = dot(P, C).sub(o.R * o.R);
      const keep = half === 'front' ? side.greaterThan(0) : side.lessThanEqual(0);
      const m = select(keep, cov, float(0));
      return vec4(col.mul(m), a.mul(m));
    })();
    mat.name = `space-ring-${half}`;
    markMrtOverlay(mat);
    // THE BODY LAYER LAW (the limb-plane split makes it exact); the front half only where it crosses its own planet
    if (half === 'front') seatBodyLayer(mat, 'over', o.R * OVER_REACH.ring); else seatBodyLayer(mat, 'behind');
    const mesh = new THREE.Mesh(geometry, mat);
    mesh.quaternion.copy(q);
    mesh.name = `space-ring-${half}`;
    mesh.renderOrder = half === 'front' ? RING_ORDER.front : RING_ORDER.back;
    mesh.frustumCulled = false;
    return mesh;
  };
  const back = make('back'), front = make('front');
  return { back, front, geometry, materials: [back.material as THREE.Material, front.material as THREE.Material] };
}

// ── orbital clouds ───────────────────────────────────────────────────────────────────────────────────────────────
/** The Commons' coverage textures (two, cross-faded) and their bake scheduler (the worker, else a main-thread slice). */
export class OrbitalCloudBaker {
  readonly W: number; readonly H: number;
  readonly texA: THREE.DataTexture; readonly texB: THREE.DataTexture;
  readonly uBlend = uniform(0);
  readonly uTime = uniform(0);
  readonly state = { bakes: 0, lastMs: 0, lastAt: 0, patchMean: 0, farMean: 0, residualRms: 0, worker: false, pending: false, error: '', override: '', seed: '', sliceRow: -1 };
  private worker: Worker | null = null;
  private seed = '';
  private nextBakeAt = 0;
  private landedAt = -1;
  private reqId = 0;
  private slice: { opts: Parameters<typeof bakeClouds>[0]; out: CloudBake | undefined; row: number; residual: Float32Array | null } | null = null;
  private disposed = false;

  constructor(W = CLOUD_TEX_W, H = CLOUD_TEX_H) {
    this.W = W; this.H = H;
    const mk = (name: string): THREE.DataTexture => {
      const t = new THREE.DataTexture(new Uint8Array(W * H * 4), W, H, THREE.RGBAFormat, THREE.UnsignedByteType);
      t.wrapS = THREE.RepeatWrapping; t.wrapT = THREE.ClampToEdgeWrapping;
      t.magFilter = THREE.LinearFilter; t.minFilter = THREE.LinearMipmapLinearFilter; t.generateMipmaps = true;
      t.anisotropy = 8; t.colorSpace = THREE.NoColorSpace; t.name = name; t.needsUpdate = true;
      return t;
    };
    this.texA = mk('orbital-clouds-a'); this.texB = mk('orbital-clouds-b');
  }
  /** Start baking the Commons' field for `seed` (a worker; a main-thread slice when a worker cannot start). */
  start(seed: string): void {
    this.seed = seed; this.state.seed = seed;
    if (!this.worker && typeof Worker !== 'undefined') {
      try {
        this.worker = new Worker(new URL('../../workers/orbital_clouds_worker.ts', import.meta.url), { type: 'module' });
        this.worker.onmessage = (ev: MessageEvent<{ id: number; data?: Uint8Array; ms?: number; patchMean?: number; farMean?: number; residualRms?: number; error?: string }>) => {
          const r = ev.data;
          this.state.pending = false;
          if (this.disposed || r.id !== this.reqId) return;
          if (r.error || !r.data) { this.state.error = r.error ?? 'no data'; return; }
          this.land(r.data, r.ms ?? 0, r.patchMean ?? 0, r.farMean ?? 0, r.residualRms ?? 0);
        };
        this.worker.onerror = (e) => { this.state.error = String(e.message ?? e).slice(0, 200); this.worker?.terminate(); this.worker = null; this.state.worker = false; this.state.pending = false; };
        this.state.worker = true;
      } catch (e) { this.worker = null; this.state.worker = false; this.state.error = String(e).slice(0, 200); }
    }
    this.nextBakeAt = 0;
  }
  private land(data: Uint8Array, ms: number, patchMean: number, farMean: number, residualRms: number): void {
    const a = this.texA.image.data as Uint8Array, b = this.texB.image.data as Uint8Array;
    a.set(b); b.set(data);
    this.texA.needsUpdate = true; this.texB.needsUpdate = true;
    this.landedAt = performance.now() / 1000;
    Object.assign(this.state, { bakes: this.state.bakes + 1, lastMs: Math.round(ms), lastAt: Date.now(), patchMean: +patchMean.toFixed(3), farMean: +farMean.toFixed(3), residualRms: +residualRms.toFixed(3), sliceRow: -1 });
  }
  /** Per frame: schedule the next bake on the wall clock, advance a main-thread slice, drive the cross-fade. */
  tick(nowS: number, tod: number, override?: string): void {
    if (this.disposed || !this.seed) return;
    (this.uTime as unknown as U<number>).value = nowS % 100_000;
    const t = performance.now() / 1000;
    (this.uBlend as unknown as U<number>).value = this.state.bakes === 0 ? 0 : cloudBlend(t - this.landedAt);
    this.state.override = override ?? '';
    if (this.slice) { this.stepSlice(); return; }
    if (this.state.pending || t < this.nextBakeAt) return;
    this.nextBakeAt = t + BAKE_EVERY_S;
    const opts = { seed: this.seed, tSec: nowS, tod, override, W: this.W, H: this.H };
    if (this.worker) { this.state.pending = true; this.worker.postMessage({ id: ++this.reqId, ...opts }); }
    else this.slice = { opts, out: undefined, row: 0, residual: null };
  }
  /** Force a bake now (the lever). */
  bakeNow(): void { this.nextBakeAt = 0; this.state.pending = false; this.reqId++; }
  /** Finish the running cross-fade at once (the bed's pictures — a fade is a time, not a look). */
  settle(): void { if (this.landedAt >= 0) this.landedAt = -1e9; (this.uBlend as unknown as U<number>).value = this.state.bakes > 0 ? 1 : 0; }
  private stepSlice(): void {
    const s = this.slice!;
    const t0 = performance.now();
    const rows = Math.max(1, Math.round(this.H / 64));
    const r = bakeClouds(s.opts, s.out, [s.row, Math.min(this.H, s.row + rows)], s.residual);
    s.out = r.bake; s.residual = r.residual; s.row += rows;
    this.state.sliceRow = s.row;
    if (s.row >= this.H) { this.slice = null; this.land(r.bake.data, performance.now() - t0, r.bake.patchMean, r.bake.farMean, r.bake.residualRms); }
  }
  /** The blended channels (R coverage, G thickness, B dust, A precipitation) at a uv (explicit gradients optional). */
  sampleNode(uvN: N, gx?: N, gy?: N): N {
    const a = gx ? (texture(this.texA, uvN) as N).grad(gx, gy) : texture(this.texA, uvN);
    const b = gx ? (texture(this.texB, uvN) as N).grad(gx, gy) : texture(this.texB, uvN);
    return mix(a, b, this.uBlend);
  }
  /** The cloud MASK at a point: the coverage carved into cells by the 3-D detail noise on the unit direction. */
  maskNode(c: N, dir: N): N {
    const drift = vec3(float(this.uTime).mul(0.0021), float(this.uTime).mul(-0.0007), float(this.uTime).mul(0.0013));
    const n = mx_fractal_noise_float(dir.mul(CLOUD_DETAIL.freq).add(drift), CLOUD_DETAIL.octaves, CLOUD_DETAIL.lacunarity, CLOUD_DETAIL.gain, 1).mul(0.5).add(0.5);
    return smoothstep(n.sub(CLOUD_DETAIL.edge), n.add(CLOUD_DETAIL.edge), c.r);
  }
  dispose(): void {
    this.disposed = true;
    this.worker?.terminate(); this.worker = null;
    disposeAfterSwap(this.texA, this.texB); // the shell that samples the two maps may still ride an in-flight submit — free them after the window, never in the teardown tick
  }
}

let black1: THREE.DataTexture | null = null;
/** A shared 1×1 black placeholder (the city lights before the sites are known) — never freed by a sector sweep. */
function blackTex(): THREE.DataTexture {
  if (black1) return black1;
  black1 = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1); black1.needsUpdate = true; black1.userData.shared = true;
  return black1;
}
/**
 * The Commons' cloud shell: a transparent sphere `CLOUD_SHELL_K` × R, to be added to the SPINNING planet mesh (its uv is
 * the night lights' and the coverage's), lit by the planet's own sun × the receiver visibility, glowing faintly over the
 * city lights at night. renderOrder 1.5 (after the ring's back half, before the shells and the front half).
 */
export function buildCloudShell(o: { R: number; set: EclipseSet; sunColor: N; clouds: OrbitalCloudBaker }): { mesh: THREE.Mesh; setCities(t: THREE.Texture): void } {
  const geo = new THREE.SphereGeometry(o.R * CLOUD_SHELL_K, 192, 96);
  const tCity = texture(blackTex(), uv());
  const uCity = uniform(0);
  const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.FrontSide });
  mat.blending = THREE.CustomBlending;
  mat.blendSrc = THREE.OneFactor; mat.blendDst = THREE.OneMinusSrcAlphaFactor; mat.blendEquation = THREE.AddEquation;
  mat.blendSrcAlpha = THREE.OneFactor; mat.blendDstAlpha = THREE.OneMinusSrcAlphaFactor; mat.blendEquationAlpha = THREE.AddEquation;
  mat.fog = false;
  mat.colorNode = Fn(() => {
    const f = planetFrame();
    const P = f.rel;
    const Nw = normalize(P);
    const V = normalize(cameraPosition.sub(positionWorld));
    const c = o.clouds.sampleNode(uv());                      // top of the shader: implicit derivatives, the mips
    const city = tCity.r.mul(1.5).mul(uCity);
    const mask = o.clouds.maskNode(c, normalize(positionGeometry));
    const muV = max(abs(dot(Nw, V)), float(0.1));
    const alpha = min(float(0.96), float(1).sub(exp(mask.mul(float(CLOUD_KAPPA.base).add(c.g.mul(CLOUD_KAPPA.thick))).negate().div(muV)))); // orbital_clouds.cloudAlphaAt
    const sun = o.set.sunAt(P);
    const ndl = dot(Nw, sun.L);
    const diffuse = clamp(ndl.add(0.12).div(1.12), 0, 1);
    const vis = o.set.visAt(P, { ring: true });
    const white = mix(vec3(0.93, 0.95, 0.98), vec3(0.86, 0.70, 0.50), c.b);   // blowing dust reads tan from orbit
    const grey = mix(float(1), float(0.74), c.a);                            // a raining deck is a greyer top
    const lit = vec3(o.sunColor).mul(white).mul(grey).mul(diffuse).mul(vis).mul(0.9);
    const night = smoothstep(0.05, -0.2, ndl);
    const glow = vec3(1.0, 0.72, 0.42).mul(city).mul(night).mul(0.9);
    return vec4(lit.add(glow).mul(alpha), alpha);
  })();
  mat.name = 'space-orbital-clouds';
  markMrtOverlay(mat);
  seatBodyLayer(mat, 'over', o.R * OVER_REACH.cloud); // THE BODY LAYER LAW: 0.8 % of a far planet's scaled radius is under one depth code
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = 'space-orbital-clouds';
  mesh.renderOrder = RING_ORDER.cloud;
  return { mesh, setCities: (t) => { tCity.value = t; (uCity as unknown as U<number>).value = 1; } };
}

// ── the per-frame controller ─────────────────────────────────────────────────────────────────────────────────────
export interface LitBody {
  key: string;
  /** The body's centre in the ABSOLUTE frame (live — a moving body updates it). */
  abs: V3;
  radius: number;
  /** The receiver set of this body (its own frame), or null (an occluder only — a world planet's standard material). */
  set: EclipseSet | null;
  ring: { rIn: number; rOut: number; normal: V3; tauScale: number } | null;
  ringBuild?: RingBuild | null;
  /** Kept for the whole ring (the reach of its receivers). */
  reachK?: number;
}
/**
 * `off`: the HOOK frame's origin in the absolute frame (`hookOrigin`) — nothing else reads it. `extra`: occluders that
 * are not registered bodies this frame (the charted worlds, the war's capitals) — absolute frame.
 */
export interface SpaceLightFrame { off: V3; ship: V3; cam: V3; extra?: ReadonlyArray<Occluder>; pixelAngle: number }

/**
 * THE HOOK'S FRAME (bug scan 2026-10-08). The hull hook evaluates at `positionWorld` — a sector point's offset-local
 * position PLUS `scene.position` (the FP64 rebase holds the scene at −origin; in the sector that is the ship, up to
 * 2,400 km) — so its uniforms are written about `off − sceneShift`. Written about `off` alone, every occluder, the ring
 * and a point sun stood −sceneShift away from the hulls they shade: no eclipse on the shadow axis, a false one beside it.
 */
export function hookOrigin(off: V3, sceneShift: V3): V3 {
  return { x: off.x - sceneShift.x, y: off.y - sceneShift.y, z: off.z - sceneShift.z };
}

/**
 * The per-frame driver: picks each receiver's occluders (the planets, the charted worlds, the war's capitals), seats the
 * sun in every frame, feeds the key light's hook in WORLD space and keeps the CPU mirrors the levers read.
 */
export class SpaceLight {
  readonly bodies: LitBody[] = [];
  readonly hook = new EclipseSet('hook');
  readonly uPixel = uniform(0.001);
  sun: EclipseSun = { kind: 'dir', dir: { x: 0.5406, y: 0.7863, z: 0.2949 }, angR: 0.0096 };
  flags = { eclipse: true, rings: true, clouds: true };
  clouds: OrbitalCloudBaker | null = null;
  /** CPU mirrors for the levers and the exposure (the ship's sun, the camera's ring transmittance). */
  shipVis = 1; camRingT = 1;
  hookPicks: string[] = [];
  picks: Record<string, string[]> = {};
  receivers = 0;
  private hookNodeCache: N = null;

  setSun(sun: EclipseSun): void { this.sun = sun; }
  addBody(b: LitBody): LitBody { this.bodies.push(b); return b; }
  removeBody(key: string): void { const i = this.bodies.findIndex((b) => b.key === key); if (i >= 0) this.bodies.splice(i, 1); }
  /** The key light's shadowNode (WORLD space — the frame `hookOrigin` gives `update`) — assign to `light.shadow.shadowNode` with `castShadow = true`. */
  hookNode(): N { return (this.hookNodeCache ??= this.hook.visAt(positionWorld, { ring: true })); }

  update(fr: SpaceLightFrame): void {
    (this.uPixel as unknown as U<number>).value = fr.pixelAngle;
    const all: Occluder[] = this.bodies.map((b) => ({ x: b.abs.x, y: b.abs.y, z: b.abs.z, r: b.radius, key: b.key }));
    if (fr.extra) for (const c of fr.extra) all.push(c);
    const on = this.flags.eclipse;
    for (const b of this.bodies) {
      if (b.ringBuild) b.ringBuild.back.visible = b.ringBuild.front.visible = this.flags.rings;
      if (!b.set) continue;
      const reach = b.radius * (b.ring ? b.ring.rOut : 1.03);
      const picks = pickOccluders(b.abs, reach, this.sun, all, 4, b.key);
      this.picks[b.key] = picks.map((p) => p.key ?? '?');
      b.set.setSun(this.sun, b.abs);
      b.set.setOccluders(picks, b.abs);
      b.set.setSelf(b.radius);
      b.set.setRing(b.ring && this.flags.rings ? { centre: { x: 0, y: 0, z: 0 }, normal: b.ring.normal, planetR: b.radius, rIn: b.ring.rIn, rOut: b.ring.rOut, tauScale: b.ring.tauScale } : null);
      b.set.setOn(on);
    }
    // THE HULL HOOK: world space (`fr.off` = hookOrigin — the fragment is positionWorld), the occluders of the ship's neighbourhood (a 100 km sphere — the true-position region)
    const picks = pickOccluders(fr.ship, 100_000, this.sun, all, 4);
    this.hookPicks = picks.map((p) => p.key ?? '?');
    this.hook.setSun(this.sun, fr.off);
    this.hook.setOccluders(picks, fr.off);
    this.hook.setOn(on);
    let ringAbs: RingSpec | null = null;
    if (this.flags.rings) {
      let best = Infinity;
      for (const b of this.bodies) {
        if (!b.ring) continue;
        const d = Math.hypot(fr.ship.x - b.abs.x, fr.ship.y - b.abs.y, fr.ship.z - b.abs.z) - b.ring.rOut * b.radius;
        if (d < best && d < 400_000) { best = d; ringAbs = { centre: { ...b.abs }, normal: b.ring.normal, planetR: b.radius, rIn: b.ring.rIn, rOut: b.ring.rOut, tauScale: b.ring.tauScale }; }
      }
    }
    this.hook.setRing(ringAbs ? { ...ringAbs, centre: { x: ringAbs.centre.x - fr.off.x, y: ringAbs.centre.y - fr.off.y, z: ringAbs.centre.z - fr.off.z } } : null);
    // CPU mirrors (absolute frame)
    const ringArg = ringAbs ? { spec: ringAbs, prof: ringProfileCpu() } : null;
    this.shipVis = receiverVisibility(fr.ship, this.sun, on ? picks : [], ringArg); // visAt's composition: the flag gates the occluders, not the ring
    this.camRingT = ringArg ? receiverVisibility(fr.cam, this.sun, [], ringArg) : 1;
  }

  /** THE HOOK needs receivers: every lit mesh under `roots` (not under a `skip` subtree) receives (1 Hz is plenty). */
  markReceivers(roots: ReadonlyArray<THREE.Object3D | null | undefined>, skip: ReadonlySet<THREE.Object3D>): number {
    let n = 0;
    const visit = (o: THREE.Object3D): void => {
      if (skip.has(o)) return;
      const m = o as THREE.Mesh;
      if (m.isMesh && isLit(m.material)) { if (!m.receiveShadow) m.receiveShadow = true; n++; }
      for (const c of o.children) visit(c);
    };
    for (const r of roots) if (r) visit(r);
    this.receivers = n;
    return n;
  }

  state(): Record<string, unknown> {
    return {
      flags: { ...this.flags }, shipVis: +this.shipVis.toFixed(4), camRingT: +this.camRingT.toFixed(4), hookPicks: [...this.hookPicks],
      receivers: this.receivers, picks: { ...this.picks },
      bodies: this.bodies.map((b) => ({ key: b.key, r: b.radius, ring: !!b.ring, set: !!b.set })),
      sun: this.sun.kind === 'dir' ? { kind: 'dir', angDeg: +((this.sun.angR * 180) / Math.PI).toFixed(3) } : { kind: 'point', radius: this.sun.radius },
      clouds: this.clouds ? { ...this.clouds.state } : null,
    };
  }
}
function isLit(m: THREE.Material | THREE.Material[] | undefined): boolean {
  const one = (x: THREE.Material): boolean => {
    const t = x as unknown as { isMeshStandardMaterial?: boolean; isMeshPhysicalMaterial?: boolean; isMeshLambertMaterial?: boolean; isMeshPhongMaterial?: boolean; isNodeMaterial?: boolean; lights?: boolean };
    return !!(t.isMeshStandardMaterial || t.isMeshPhysicalMaterial || t.isMeshLambertMaterial || t.isMeshPhongMaterial || (t.isNodeMaterial && t.lights));
  };
  return Array.isArray(m) ? m.some(one) : !!m && one(m);
}

/** One pixel's angle for a perspective camera (the ring's edge-on coverage). */
export function pixelAngle(fovDeg: number, heightPx: number): number {
  return (2 * Math.tan((fovDeg * Math.PI) / 360)) / Math.max(1, heightPx);
}
/** The sphere uv of a unit direction (three's SphereGeometry: u = atan2(z, −x)/2π, v = 1 − acos(y)/π) — nodes. */
export function dirUvN(d: N): N {
  const u = atan(d.z, d.x.negate()).div(2 * Math.PI);
  return vec2(u.fract(), float(1).sub(acos(clamp(d.y, -1, 1)).div(Math.PI)));
}
